import { type GalleryGroupType } from '@/defs/gallery'
import { type IconType } from '@/defs/icon'
import { type RecRoleType } from '@/defs/rec'
import { slugify } from '@/utils/commonUtil'

type SlugMapType = {
  fn: (s: string) => boolean
  icon: IconType
  role?: RecRoleType
}

const SLUG_MAP: SlugMapType[] = [
  { fn: (s) => s.includes('new-commander'), icon: 'chessQueen', role: 'commander' },
  { fn: (s) => s.includes('new-card'), icon: 'gift' },
  { fn: (s) => s.includes('commander'), icon: 'chessKing', role: 'commander' },
  { fn: (s) => s.includes('enchantment'), icon: 'type-enchantment' },
  { fn: (s) => s.includes('creature'), icon: 'type-creature' },
  { fn: (s) => s.includes('game-changers'), icon: 'gem' },
  { fn: (s) => s.includes('lift'), icon: 'anglesUp' },
  { fn: (s) => s.includes('synergy'), icon: 'wandMagicSparkles' },
  { fn: (s) => s.includes('instant'), icon: 'type-instant' },
  { fn: (s) => s.includes('artifact'), icon: 'type-artifact' },
  { fn: (s) => s.includes('planeswalker'), icon: 'type-planeswalker' },
  { fn: (s) => s.includes('sorcer'), icon: 'type-sorcery' },
  { fn: (s) => s.includes('top-card'), icon: 'star' },
  { fn: (s) => s.includes('battle'), icon: 'type-battle' },
  { fn: (s) => s.includes('partner-with'), icon: 'peopleArrows', role: 'commander' },
  { fn: (s) => s.includes('partners-with'), icon: 'peopleArrows', role: 'commander' },
  { fn: (s) => s.includes('partner'), icon: 'hatCowboy' },
  { fn: (s) => s.includes('choose-a-background'), icon: 'chessKing' },
  { fn: (s) => s.includes('background'), icon: 'chessQueen' },
  { fn: (s) => s.includes('friends-forever'), icon: 'heart' },
  { fn: (s) => s.includes('doctor'), icon: 'stethoscope' },
  { fn: (s) => s.includes('survivor'), icon: 'biohazard' },
  { fn: (s) => s.includes('father-son'), icon: 'family' },
  { fn: (s) => s.includes('cards'), icon: 'cards' },
  { fn: (s) => s.includes('reprints'), icon: 'copy' },
  { fn: (s) => s.includes('castle'), icon: 'castle' },
  { fn: (s) => s.includes('hideaway'), icon: 'eye' },
  { fn: (s) => s.includes('multiple-mana'), icon: 'clone' },
  { fn: (s) => s.includes('channel'), icon: 'wandMagicSparkles' },
  { fn: (s) => s.includes('destruction'), icon: 'skull' },
  { fn: (s) => s.includes('fetch'), icon: 'search' },
  { fn: (s) => s.includes('ramp'), icon: 'turnUp' },
  { fn: (s) => s.includes('zendikar-named'), icon: 'landmark' },
  { fn: (s) => s.includes('copy-land'), icon: 'copy' },
  { fn: (s) => s.includes('planet'), icon: 'planetRinged' },
  { fn: (s) => s.includes('cycling-land'), icon: 'repeat' },
  { fn: (s) => s.includes('legendary-land'), icon: 'chessKing' },
  { fn: (s) => s.includes('panorama'), icon: 'sun' },
  { fn: (s) => s.includes('command-tower'), icon: 'castle' },
  { fn: (s) => s.includes('sacrifice-cost'), icon: 'knife' },
  { fn: (s) => s.includes('bounce-land'), icon: 'droplet' },
  { fn: (s) => s.includes('gemstone'), icon: 'gem' },
  { fn: (s) => s.includes('adamant'), icon: 'handshake' },
  { fn: (s) => s.includes('hidden-land'), icon: 'ghost' },
  { fn: (s) => s.includes('sac-land'), icon: 'trash' },
  { fn: (s) => s.includes('adventure-land'), icon: 'shield' },
  { fn: (s) => s.includes('spheres'), icon: 'circle' },
  { fn: (s) => s.includes('rainbow-land'), icon: 'rainbow' },
  { fn: (s) => s.includes('depletion-land'), icon: 'clock' },
  { fn: (s) => s.includes('pain-land'), icon: 'skullCrossbones' },
  { fn: (s) => s.includes('tron-land'), icon: 'dumbbell' },
  { fn: (s) => s.includes('artifact-land'), icon: 'type-artifact' },
  { fn: (s) => s.includes('exotic-orchard'), icon: 'tree' },
  { fn: (s) => s.includes('villages'), icon: 'house' },
  { fn: (s) => s.includes('road'), icon: 'route' },
  { fn: (s) => s.includes('replacement-land'), icon: 'clone' },
  { fn: (s) => s.includes('thriving-land'), icon: 'rankingStar' },
  { fn: (s) => s.includes('locus-land'), icon: 'moon' },
  { fn: (s) => s.includes('gates'), icon: 'city' },
  { fn: (s) => s.includes('vivid-land'), icon: 'sun' },
  { fn: (s) => s.includes('storage-land'), icon: 'plus' },
  { fn: (s) => s.includes('typal-land'), icon: 'family' },
  { fn: (s) => s.includes('utility-land'), icon: 'toolbox' },
  { fn: (s) => s.includes('land'), icon: 'type-land' },
  { fn: (s) => s.includes('past-week'), icon: 'clock' },
  { fn: (s) => s.includes('past-month'), icon: 'clock' },
  { fn: (s) => s.includes('staples'), icon: 'stapler' },
  { fn: (s) => s.includes('mana-rock'), icon: 'treasureChest' },
]

const CATEGORY_REPLACEMENTS: Record<string, string> = {
  'High Lift Cards': 'High Lift',
  'High Synergy Cards': 'High Synergy',
}

export const parseGalleryCategory = (labelProp?: string): Omit<GalleryGroupType, 'recs'> => {
  const rawLabel = (labelProp || '').trim()
  const label = CATEGORY_REPLACEMENTS[rawLabel] || rawLabel
  const slug = slugify(labelProp || '')

  return {
    icon: SLUG_MAP.find((m) => m.fn(slug))?.icon || 'diamond',
    label: label || 'Group',
    slug,
  }
}

export const parseGalleryCategoryRole = (labelProp?: string): RecRoleType | undefined =>
  SLUG_MAP.find((m) => m.fn(slugify(labelProp || '')))?.role
